// The built-in strategies.
//
// Classic is the pad the app shipped with. Most of the rest are model-driven:
// each says, in data, where its probability comes from, how thin an edge it
// will take, and how many legs it is willing to stack. Elo Edge is the first
// that needs a state fetched from the server before it can run at all.
//
// Not everything in this file prints. `CATALOGUE` is what Today deals;
// `RETIRED` at the foot is the documents that have stopped being offered and
// cannot be deleted, because records, forks and goldens still name them. Both
// are read through `findStrategy`.
//
// Favourites and Two Long Shots is the exception in the other direction — a
// SHAPE, with no model and no value screen at all. It is the one strategy here
// that separates the two dials Classic's tiers move together: how many legs,
// and how long each leg is.

import { CLASSIC } from './classic.js';
// ¼ Kelly, 2% a ticket, 10% of the bankroll across the day — read out of the
// one place they are typed (engine review #13). A strategy that measures an
// edge stakes by it; with no bankroll in Settings the engine stakes flat and
// says so, which is what every one of these did before.
import { KELLY_STAKING } from '../kelly.js';
// The three that deal from the RACING CARD rather than the sports board. They
// are documents like everything else here; the one of them that needs code of
// its own keeps it beside them, not in this file. See `strategies/racing.js`.
import { RACING_ALL_UP, RACING_MISPRICED, RACING_VALUE } from './racing.js';

/**
 * A shape, not a value screen.
 *
 * Classic's tiers turn one dial: "outrageous" is always both more legs and
 * longer ones. This turns the two separately — five legs, of which exactly two
 * are long shots — so "many legs, all short favourites" and "a few short legs
 * plus a couple of long shots" are different strategies rather than different
 * rungs of one ladder.
 *
 * WHY minEdge AND minEv ARE ZERO, and why that is not a loose screen: with
 * `model: 'none'` the probability IS the book's own devigged price, so every
 * leg's edge is exactly 0 and its EV is exactly minus the book's margin. There
 * is no opinion here to measure an edge with, so `runStrategy` does not ask
 * these thresholds of a no-model strategy at all, and every leg that fits the
 * price band is in the draw. What chooses the legs is the leg rule: the
 * likeliest price in each band, from different matches.
 */
export const FAVOURITES_TWO_LONG_SHOTS = {
  id: 'favourites-two-long-shots',
  name: 'Favourites and Two Long Shots',
  version: 1,
  builtin: true,
  description: 'Five legs: three short favourites and two long shots. A shape, not a value screen.',
  filters: {
    sports: [],
    markets: ['h2h'],
    priceBand: [1.01, 4.00],
    daysToKickoff: [0, 0],
    rules: [],
  },
  probabilitySource: { model: 'none', params: {} },
  activeLeg: { minEdge: 0, minEv: 0, devig: 'multiplicative' },
  legRule: {
    type: 'mix', legs: 5, longShots: 2, shortBand: [1.01, 1.60], longBand: [2.00, 4.00],
  },
  combiner: {
    minLegs: 3, maxLegs: 5, tickets: 1, distinctEvents: true, excludeSGM: true,
  },
  staking: { type: 'flat' },
  bookmakerRule: 'single',
  slots: 1,
};

/**
 * Three strategies' worth of one idea: the market's own fair price against the
 * best price on the board.
 *
 * Value Favourites, Optimal Legs and Consensus Edge all ran market consensus
 * and all took a leg the moment it cleared 2% edge and 2% EV. What separated
 * them was a favourites-only rule, a leg count and which book the ticket was
 * written at — and on Today that read as three sections with nothing to choose
 * between them. This is Consensus Edge's model and book (best price, the one
 * that actually raises the EV of a leg), Optimal Legs' Whelan leg count, and
 * the price band all three agreed on.
 *
 * VALUE FAVOURITES' FAVOURITES-ONLY RULE DID NOT COME ACROSS. It was a rule,
 * not a band: `isFavourite` threw away every leg the market called an underdog,
 * which is a narrower strategy than this one rather than a setting of it. A
 * punter who wants it back has the rule in the editor and a fork to put it on.
 *
 * The three ids still resolve — see `aliases.js` — so a trail, a fork's
 * `baseId` and a saved backtest spec all still find their way here.
 *
 * BEST BOOK, NOT BEST PRICE (engine review #4). It shopped leg by leg and
 * printed a five-leg multi whose combined price existed at no counter: no
 * Australian book will fold three other books' quotes into one bet slip. It now
 * re-prices every leg at each book on the board in turn and keeps the book whose
 * ticket is worth the most — which is a narrower search, and usually a shorter
 * ticket, because a book that is longest on two legs and not pricing the third
 * cannot print the ticket at all.
 */
export const MARKET_EDGE = {
  id: 'market-edge',
  name: 'Market edge',
  version: 1,
  builtin: true,
  description: 'The market\'s own average price, with the vig taken out, against the one book paying the most for the whole ticket.',
  filters: {
    sports: [],
    markets: ['h2h'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [],
  },
  probabilitySource: { model: 'market-consensus', params: { devig: 'power' } },
  activeLeg: { minEdge: 0.02, minEv: 0.02, devig: 'power' },
  legRule: { type: 'whelan' },
  combiner: { minLegs: 1, maxLegs: 5, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { ...KELLY_STAKING },
  bookmakerRule: 'best-book',
  slots: 1,
};

export const VALUE_FAVOURITES = {
  id: 'value-favourites',
  name: 'Value Favourites',
  version: 1,
  builtin: true,
  description: 'Favourites only. The favourite-longshot bias means the margin is loaded on the underdog, so favourites are closer to fair.',
  filters: {
    sports: [],
    markets: ['h2h'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [{ field: 'isFavourite', op: 'eq', value: true }],
  },
  probabilitySource: { model: 'market-consensus', params: { devig: 'shin' } },
  activeLeg: { minEdge: 0.02, minEv: 0.02, devig: 'shin' },
  legRule: { type: 'fixed', legs: 3 },
  combiner: { minLegs: 2, maxLegs: 3, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { type: 'flat' },
  bookmakerRule: 'single',
  slots: 1,
};

export const OPTIMAL_LEGS = {
  id: 'optimal-legs',
  name: 'Optimal Legs',
  version: 1,
  builtin: true,
  description: 'Leg count follows the maths: more legs only when every leg is short enough (Whelan 2026). May be a single.',
  filters: {
    sports: [],
    markets: ['h2h'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [],
  },
  probabilitySource: { model: 'market-consensus', params: { devig: 'power' } },
  activeLeg: { minEdge: 0.02, minEv: 0.02, devig: 'power' },
  legRule: { type: 'whelan' },
  combiner: { minLegs: 1, maxLegs: 6, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { type: 'flat' },
  bookmakerRule: 'single',
  slots: 1,
};

export const CONSENSUS_EDGE = {
  id: 'consensus-edge',
  name: 'Consensus Edge',
  version: 1,
  builtin: true,
  description: 'Model versus market at the best price on the board.',
  filters: {
    sports: [],
    markets: ['h2h'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [],
  },
  probabilitySource: { model: 'market-consensus', params: { devig: 'power' } },
  activeLeg: { minEdge: 0.02, minEv: 0.02, devig: 'power' },
  legRule: { type: 'whelan' },
  combiner: { minLegs: 1, maxLegs: 5, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { type: 'flat' },
  // Best BOOK, for the reason Market edge gives above: this document is the one
  // Market edge was merged from, and a retired strategy that cannot be validated
  // is a backtest spec that stops running.
  bookmakerRule: 'best-book',
  slots: 1,
};

export const SHARP_ANCHOR = {
  id: 'sharp-anchor',
  name: 'Sharp Anchor',
  version: 1,
  builtin: true,
  description: 'Bet only where your book is longer than the sharpest price on the board (Betfair or Pinnacle) after their margin is stripped.',
  filters: {
    sports: [],
    markets: ['h2h'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [],
  },
  // Anchored on the exchange alone. Pinnacle is not in the AU region the app
  // buys, and a strategy anchored on a book that is never on the board said
  // "nothing fits" every day of its life.
  probabilitySource: { model: 'sharp-anchor', params: { devig: 'power', books: ['betfair_ex_au'] } },
  activeLeg: { minEdge: 0.02, minEv: 0.02, devig: 'power' },
  legRule: { type: 'whelan' },
  combiner: { minLegs: 1, maxLegs: 4, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { type: 'flat' },
  bookmakerRule: 'single',
  slots: 1,
};

export const ELO_EDGE = {
  id: 'elo-edge',
  name: 'Elo Edge',
  version: 1,
  builtin: true,
  description: 'Team ratings from every past result, with margin, home ground and off-season regression. AFL, NRL, NBL, BBL.',
  filters: {
    sports: ['afl', 'nrl', 'nbl', 'bbl'],
    markets: ['h2h'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [],
  },
  probabilitySource: { model: 'elo', params: {} },
  activeLeg: { minEdge: 0.02, minEv: 0.02, devig: 'power' },
  legRule: { type: 'whelan' },
  combiner: { minLegs: 1, maxLegs: 4, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { ...KELLY_STAKING },
  bookmakerRule: 'single',
  slots: 1,
};

export const DIXON_COLES_EDGE = {
  id: 'dixon-coles-edge',
  name: 'Dixon-Coles Edge',
  version: 1,
  builtin: true,
  description: 'Poisson goals with the Dixon-Coles low-score correction and time decay, fitted on results. Premier League.',
  filters: {
    sports: ['epl'],
    markets: ['h2h'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [],
  },
  probabilitySource: { model: 'dixon-coles', params: {} },
  activeLeg: { minEdge: 0.02, minEv: 0.02, devig: 'power' },
  legRule: { type: 'whelan' },
  combiner: { minLegs: 1, maxLegs: 4, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { ...KELLY_STAKING },
  bookmakerRule: 'single',
  slots: 1,
};

/**
 * The one strategy here that is not trying to pick a winner.
 *
 * Every other model in the app is fitted on who won, and on that label none of
 * them beats a closing line — which is what the literature says will happen and
 * what the goldens keep saying out loud. `close-line` is fitted on where the
 * PRICE ended up instead (see `models/closeLine.js`), so this is Market edge's
 * shape with a different opinion bolted into the same socket: the market's own
 * consensus is the prior, the model's claim is a correction to it, and the
 * correction only applies at a weight a backtest measured.
 *
 * IT STILL GOES THROUGH THE SAME GATE, and that is the honest part. The
 * encompassing fit asks whether this model helps predict RESULTS given the
 * market's price — not whether it predicts the close, which it is built to do —
 * and under t = 1.96 per sport it prints nothing at all. A model that nails the
 * close and adds nothing to the board's own forecast has told you the market
 * moves; everybody knew that.
 *
 * THREE LEGS, NOT FIVE. The claim here is about a price moving between now and
 * the jump, and every leg stacked on it multiplies in another book's margin
 * against a correction measured in a point or two. Whelan still chooses the
 * count; this is where the ladder is allowed to stop.
 */
export const CLOSE_CHASER = {
  id: 'close-chaser',
  name: 'Close chaser',
  version: 1,
  builtin: true,
  description: 'Bets where the price is expected to shorten before the start. It does not know who wins.',
  filters: {
    sports: [],
    markets: ['h2h'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [],
  },
  probabilitySource: { model: 'close-line', params: {} },
  activeLeg: { ...MARKET_EDGE.activeLeg },
  legRule: { type: 'whelan' },
  combiner: { minLegs: 1, maxLegs: 3, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { ...KELLY_STAKING },
  bookmakerRule: 'best-book',
  slots: 1,
};

/**
 * Twenty-odd people who model the AFL for nothing, against the price.
 *
 * Market edge's shape with the crowd in the socket (`models/squiggleEnsemble.js`):
 * the market's consensus is the prior, the weighted mean of Squiggle's public
 * tipsters is the correction, and the correction only applies at the weight a
 * backtest measured. The tips arrive as a FEATURE on the match — fetched once a
 * day by the server, never by the phone — so a board nobody has joined them
 * onto produces no candidate at all rather than a guess.
 *
 * AFL ONLY, because Squiggle is an AFL project and there is no crowd of free
 * public models over any other code this app covers.
 *
 * WHY IT MIGHT BE WORTH ANYTHING: this is the one market in the app that no
 * overseas syndicate is pushing, and these are twenty-odd forecasters with no
 * money on the game and no reason to make the same mistake. WHY IT MIGHT NOT:
 * the Australian books read Squiggle too. Nobody has published which, and the
 * gate is what decides it here.
 */
export const MODEL_CROWD = {
  id: 'model-crowd',
  name: 'Model crowd',
  version: 1,
  builtin: true,
  description: 'What twenty-odd public AFL models think, weighted by how well each has been forecasting, against the board.',
  filters: {
    sports: ['afl'],
    markets: ['h2h'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [],
  },
  probabilitySource: { model: 'squiggle-ensemble', params: {} },
  activeLeg: { ...MARKET_EDGE.activeLeg },
  legRule: { type: 'whelan' },
  combiner: { ...MARKET_EDGE.combiner },
  staking: { ...KELLY_STAKING },
  bookmakerRule: 'best-book',
  slots: 1,
};

/**
 * The one durable edge, printed as what it is: not a multi.
 *
 * Every other strategy here stacks legs. This one refuses to: a bookmaker
 * whose price is above what the rest of the board says the side is worth is a
 * bet on its own, and putting a second leg on top of it multiplies in another
 * book's margin and throws most of the edge away. So the leg rule is
 * `singles` — up to three one-leg tickets, one per match, best edge first,
 * each at the one book that is out of line.
 *
 * `minBooks: 3` is what makes the claim sayable. The market consensus already
 * excludes the book being bet at, so two books on a game leaves one voter, and
 * one book calling another one wrong is not a market, it is a coin toss with
 * an opinion. Three voters is the floor the detector uses too.
 *
 * `minEv: 0.03` is where "3% over the odds" actually lives. The engine's edge
 * is in probability points, which is a different quantity from a price being
 * 3% too long; EV — probability × price − 1 — is the one that says "this pays
 * three cents in the dollar more than it is worth".
 */
export const MISPRICED_ODDS = {
  id: 'mispriced-odds',
  name: 'Mispriced odds',
  version: 1,
  builtin: true,
  description: 'One leg at a time, at the one book paying over the odds. Nothing about the teams.',
  filters: {
    sports: [],
    markets: ['h2h'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [],
  },
  probabilitySource: { model: 'market-consensus', params: { devig: 'power' } },
  // The same test the Matches highlight applies: 3% over the market's fair
  // price, which is the EV form. `minEdge` is probability points and at long
  // prices the two diverge sharply — a 3.20 that is 4.3% over the odds is only
  // 1.5 points — so an edge floor here would flag a match on the board and
  // then print no ticket for it. Zero, on purpose.
  activeLeg: { minEdge: 0, minEv: 0.03, devig: 'power' },
  legRule: { type: 'singles', max: 3 },
  combiner: { minLegs: 1, maxLegs: 1, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { ...KELLY_STAKING },
  bookmakerRule: 'best-price',
  minBooks: 3,
  slots: 3,
};

/**
 * What Today prints: seven questions about the sports board, then three about
 * the racing card. Classic (no opinion, just a shape), Market edge (the market
 * against itself), two models of who wins, one model of where the price is
 * going, one crowd of other people's models, and the one that refuses to stack
 * legs — then the same three ideas over races: value, a shape, and the singles.
 *
 * THE RACING THREE PRINT NOTHING UNTIL A CARD IS LOADED, and say so where a
 * blank section says everything else ("No racing card yet — open the Racing
 * tab"). They are in the catalogue rather than behind the tab's own flag
 * because a strategy that exists only while a screen is open is a strategy the
 * paper trail cannot judge.
 */
export const CATALOGUE = [
  CLASSIC, MARKET_EDGE, ELO_EDGE, DIXON_COLES_EDGE, CLOSE_CHASER, MODEL_CROWD, MISPRICED_ODDS,
  RACING_VALUE, RACING_ALL_UP, RACING_MISPRICED,
];

export { RACING_VALUE, RACING_ALL_UP, RACING_MISPRICED };

/**
 * Built-ins that no longer print but have not stopped EXISTING.
 *
 * Every one of them is still somebody's record, somebody's fork's `baseId` or
 * somebody's saved backtest spec, and a golden fixture is pinned on two of
 * them. So the documents stay exactly as they were and `findStrategy` still
 * answers with them — a backtest asked for Optimal Legs gets Optimal Legs, not
 * Market edge wearing its name, because re-running an old spec against a
 * different document is a silent change of answer.
 *
 * Three of them merged (`aliases.js`); Favourites and Two Long Shots has no
 * model and read as a strategy beside ones with an opinion; Sharp Anchor is
 * parked until the exchange it is anchored on is on the board — nothing in the
 * AU region carries Betfair or Pinnacle today, so it said "nothing fits" every
 * day of its life. Labs' `SHARP_ANCHOR` flag puts it back on the strategies
 * list, and the `sharp-anchor` MODEL never went anywhere.
 */
export const RETIRED = [
  FAVOURITES_TWO_LONG_SHOTS, VALUE_FAVOURITES, OPTIMAL_LEGS, CONSENSUS_EDGE, SHARP_ANCHOR,
];

/** A built-in strategy by id — the printing ones and the retired ones — or null. */
export function findStrategy(id) {
  return CATALOGUE.find((s) => s.id === id) ?? RETIRED.find((s) => s.id === id) ?? null;
}
